import { Interval } from "luxon";
import type { ReactNode } from "react";
import { Trans } from "react-i18next";
import { getPtoStaffingDays } from "../../../utils/dateUtils";
import { formatNameList } from "../../../utils/formatNameList";
import {
	ChangesSource,
	type LedgerEntry,
	type OriginDetails,
	type PostNumbers,
	RequestOriginKind,
	StatsMode,
	type TimeOffCandidate,
} from "../../../utils/models";
import { CALL_OFF_CHANNEL_KEY, formatHppd, local, type OriginCopyContext, ratioRoleLabel, shiftLabel, weekdayOf } from "./requestOriginCopy";

const BOLD = { b: <b /> };
const MONTH_DAY = { month: "short", day: "numeric" } as const;

type Details<K extends RequestOriginKind> = Extract<OriginDetails, { kind: K }>;

/** A sentence with bold runs. A numeric `count` picks the plural form, as `t` does. */
const say = (i18nKey: string, values: Record<string, string | number>): ReactNode => (
	<Trans i18nKey={i18nKey} values={values} components={BOLD} count={typeof values.count === "number" ? values.count : undefined} />
);

/** Sentences that read on from each other ("HPPD was 3.10 … Auto-post works below …"), spaced like running text. */
const run = (...parts: ReactNode[]): ReactNode => (
	<>
		{parts.map((part, index) => (
			// biome-ignore lint/suspicious/noArrayIndexKey: a fixed sequence of sentences; nothing reorders them.
			<span key={index}>
				{index > 0 && " "}
				{part}
			</span>
		))}
	</>
);

/** The numbers a post rested on, as the day stood just before it. */
export const numbersSentence = (numbers: PostNumbers, ctx: OriginCopyContext): ReactNode => {
	if (numbers.kind === StatsMode.PPD_HPPD) {
		return say("request.origin.numbers.hppd", {
			day: weekdayOf(numbers.staffing_date, ctx),
			before: formatHppd(numbers.hppd_before_post),
			target: formatHppd(numbers.hppd_target),
		});
	}
	const values = {
		required: numbers.required,
		role: ratioRoleLabel(numbers.job_id, numbers.role_group, ctx),
		scheduled: numbers.scheduled,
		posted: numbers.posted,
	};
	if (numbers.location === null) return say("request.origin.numbers.ratio_no_location", values);
	return say("request.origin.numbers.ratio", { ...values, location: numbers.location });
};

const autoPostSentence = (details: Details<RequestOriginKind.AutoPost>, ctx: OriginCopyContext): ReactNode => {
	const { numbers } = details;
	const why =
		numbers.kind === StatsMode.PPD_HPPD
			? say("request.origin.numbers.hppd_threshold", { percent: numbers.threshold_percent, threshold: formatHppd(numbers.threshold_hppd) })
			: ctx.t("request.origin.sentence.auto_post_ratio");
	const counts =
		details.recommended_count !== null && details.recommended_count !== details.posted_count
			? say("request.origin.sentence.auto_post_counts", { recommended: details.recommended_count, posted: details.posted_count })
			: null;
	return run(numbersSentence(numbers, ctx), why, ...(counts === null ? [] : [counts]));
};

const fromRecommendationSentence = (details: Details<RequestOriginKind.FromRecommendation>, entry: LedgerEntry, ctx: OriginCopyContext): ReactNode => {
	const name = entry.actor === null ? null : ctx.personName(entry.actor);
	const suffix = name === null ? "_anonymous" : "";
	const who =
		details.recommended_count !== null && details.posted_count !== null
			? say(`request.origin.sentence.from_recommendation_counts${suffix}`, {
					recommended: details.recommended_count,
					posted: details.posted_count,
					...(name === null ? {} : { name }),
				})
			: say(`request.origin.sentence.from_recommendation${suffix}`, name === null ? {} : { name });
	return run(who, details.numbers === null ? ctx.t("request.origin.numbers.missing") : numbersSentence(details.numbers, ctx));
};

/** The schedule days the time off covers, as the time off screens count them: its end is exclusive, so one day off reads "Oct 15", not "Oct 15 – 16". */
const datesOf = (candidate: TimeOffCandidate, ctx: OriginCopyContext): string | null => {
	if (candidate.starts_at === null || candidate.ends_at === null) return null;
	const days = getPtoStaffingDays(local(candidate.starts_at, ctx), local(candidate.ends_at, ctx), ctx.dayStart);
	const first = days.at(0);
	const last = days.at(-1);
	// DEFAULT: a time off that ends before it starts (bad data, which exists) covers no day; it reads as undated.
	if (first === undefined || last === undefined) return null;
	return first.hasSame(last, "day") ? first.toLocaleString(MONTH_DAY) : Interval.fromDateTimes(first, last).toLocaleString(MONTH_DAY);
};

const timeOffSentence = (details: Details<RequestOriginKind.TimeOff>, entry: LedgerEntry, ctx: OriginCopyContext): ReactNode => {
	const names = details.candidates.map((candidate) => ctx.personName(candidate.nurse));
	if (details.ambiguous) {
		return say("request.origin.sentence.time_off_ambiguous", { nurses: new Intl.ListFormat(ctx.locale, { type: "disjunction" }).format(names) });
	}
	const [only] = details.candidates;
	if (details.candidates.length !== 1 || only === undefined) {
		// Every nurse named opened one of these slots: all of them, not one of them.
		return say(`request.origin.sentence.time_off_several${entry.actor === null ? "_anonymous" : ""}`, {
			nurses: formatNameList(names, ctx.locale),
			...(entry.actor === null ? {} : { actor: ctx.personName(entry.actor) }),
		});
	}
	const dates = datesOf(only, ctx);
	const key = `request.origin.sentence.time_off${dates === null ? "_undated" : ""}${entry.actor === null ? "_anonymous" : ""}`;
	return say(key, {
		nurse: ctx.personName(only.nurse),
		...(dates === null ? {} : { dates }),
		...(entry.actor === null ? {} : { actor: ctx.personName(entry.actor) }),
	});
};

const callOffSentence = (details: Details<RequestOriginKind.CallOff>, short: boolean, ctx: OriginCopyContext): ReactNode => {
	const channelKey = CALL_OFF_CHANNEL_KEY[details.channel];
	const values = { nurse: ctx.personName(details.nurse), shift: shiftLabel(details.shift_start, details.shift_end, ctx) };
	const key = `request.origin.sentence.call_off${short ? "_short" : ""}`;
	if (channelKey === null) return say(`${key}_no_channel`, values);
	return say(key, { ...values, channel: ctx.t(`request.origin.channel.${channelKey}`) });
};

const filledSentence = (details: Details<RequestOriginKind.Filled>, entry: LedgerEntry, ctx: OriginCopyContext): ReactNode => {
	const nurse = ctx.personName(details.nurse);
	if (details.approved_via === ChangesSource.AutoApprove) return say("request.origin.sentence.filled_auto", { nurse });
	if (details.approved_via === ChangesSource.Integration) return say("request.origin.sentence.filled_agency", { nurse });
	if (entry.actor === null) return say("request.origin.sentence.filled_anonymous", { nurse });
	return say("request.origin.sentence.filled", { nurse, actor: ctx.personName(entry.actor) });
};

const canceledSentence = (details: Details<RequestOriginKind.Canceled>, entry: LedgerEntry, ctx: OriginCopyContext): ReactNode => {
	if (entry.actor !== null) return say("request.origin.sentence.canceled", { name: ctx.personName(entry.actor) });
	return ctx.t(details.source === ChangesSource.Integration ? "request.origin.sentence.canceled_agency" : "request.origin.sentence.canceled_anonymous");
};

/** The same sentence with or without the person who acted. */
const withActor = (key: string, entry: LedgerEntry, ctx: OriginCopyContext, values: Record<string, string | number>): ReactNode =>
	entry.actor === null ? say(`${key}_anonymous`, values) : say(key, { ...values, name: ctx.personName(entry.actor) });

/** One entry as a sentence. `short` is the one-line form a call-off takes as a step of the timeline. */
export const sentence = (entry: LedgerEntry, short: boolean, ctx: OriginCopyContext): ReactNode => {
	const details = entry.details;
	switch (details.kind) {
		case RequestOriginKind.CallOff:
			return callOffSentence(details, short, ctx);
		case RequestOriginKind.TimeOff:
			return timeOffSentence(details, entry, ctx);
		case RequestOriginKind.Deactivation: {
			const values = { nurse: ctx.personName(details.nurse), count: details.shifts_removed };
			return withActor("request.origin.sentence.deactivation", entry, ctx, values);
		}
		case RequestOriginKind.AutoPost:
			return autoPostSentence(details, ctx);
		case RequestOriginKind.FromRecommendation:
			return fromRecommendationSentence(details, entry, ctx);
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.PostedByAnnie:
		case RequestOriginKind.MergedIn:
		case RequestOriginKind.MergedAway:
			return withActor(`request.origin.sentence.${details.kind}`, entry, ctx, {});
		case RequestOriginKind.ReplaceRequest:
			return say("request.origin.sentence.replace_request", {
				nurse: ctx.personName(details.nurse),
				shift: shiftLabel(details.shift_start, details.shift_end, ctx),
			});
		case RequestOriginKind.SwapRequest: {
			const values = { nurse: ctx.personName(details.nurse), shift: shiftLabel(details.shift_start, details.shift_end, ctx) };
			if (details.counterpart === null) return say("request.origin.sentence.swap_request_no_counterpart", values);
			const { counterpart } = details;
			return say("request.origin.sentence.swap_request", {
				...values,
				counterpart: ctx.personName(counterpart.nurse),
				counterShift: shiftLabel(counterpart.shift_start, counterpart.shift_end, ctx),
			});
		}
		case RequestOriginKind.ReplacingAgency: {
			const nurse = ctx.personName(details.nurse);
			if (details.agency_name === null) return say("request.origin.sentence.replacing_agency_no_agency", { nurse });
			return say("request.origin.sentence.replacing_agency", { nurse, agency: details.agency_name });
		}
		case RequestOriginKind.Agency:
			if (details.from_count === 0) return say("request.origin.sentence.agency", { agency: details.agency_name });
			return say("request.origin.sentence.agency_raised", { agency: details.agency_name, from: details.from_count, to: details.to_count });
		case RequestOriginKind.AgencyCanceled:
			return say("request.origin.sentence.agency_canceled", { nurse: ctx.personName(details.nurse), agency: details.agency_name });
		case RequestOriginKind.CountRaised:
		case RequestOriginKind.CountLowered:
		case RequestOriginKind.AgencySyncLowered:
			return ctx.t("request.origin.sentence.count_changed", { from: details.from_count, to: details.to_count });
		case RequestOriginKind.CountAdjusted:
			return ctx.t("request.origin.sentence.count_adjusted");
		case RequestOriginKind.Filled:
			return filledSentence(details, entry, ctx);
		case RequestOriginKind.Canceled:
			return canceledSentence(details, entry, ctx);
		case RequestOriginKind.NotRecorded:
			return ctx.t("request.origin.sentence.not_recorded");
		case RequestOriginKind.Unknown:
			return withActor("request.origin.sentence.unknown", entry, ctx, { from: details.from_open, to: details.to_open });
		default: {
			const unhandled: never = details;
			throw new Error(`Unhandled origin ${JSON.stringify(unhandled)}`);
		}
	}
};
